import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  CreditCard, RefreshCw, Layers, ShieldCheck, Bell, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const BillingPaymentsPage = () => {
  const benefits = [
    {
      icon: ShieldCheck,
      title: 'Secure verification + policy enforcement',
      description:
        'Every billing action is preceded by identity verification and evaluated against configurable refund eligibility, proration, and dispute rules before execution.',
    },
    {
      icon: CreditCard,
      title: 'Real-time billing and subscription actions',
      description:
        'Pull live invoice data, payment status, and subscription details instantly, then execute allowed actions - refunds, plan changes, invoice links - in real time.',
    },
    {
      icon: Bell,
      title: 'Automated case creation and human handoff',
      description:
        'Every interaction generates a logged case with billing context. Disputes and chargebacks are escalated to agents with the full billing history pre-loaded.',
    },
  ];

  const capabilities = [
    'Integrate CRM + billing/subscription platform + payment gateway + ticketing',
    'Define refund eligibility, proration, cancellation reasons, and dispute flows',
    'Configure intents: invoice, refund status, payment failed, plan change, cancel',
    'Secure tokenized payment handling, PII controls, audit trails',
    'Pilot top billing intents → expand to full lifecycle workflows',
  ];

  const features = [
    {
      icon: CreditCard,
      title: 'Invoice & Payment Lookup',
      desc: 'Retrieve invoice history, payment status, failed charge details, and outstanding balances in real time from the connected billing platform.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: RefreshCw,
      title: 'Refund & Dispute Automation',
      desc: 'Evaluate refund eligibility against configured rules and initiate refund or dispute intake workflows - with policy guardrails and audit logging throughout.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Layers,
      title: 'Subscription Lifecycle',
      desc: 'Handle upgrades, downgrades, pauses, renewals, and cancellations with proration calculations and confirmation notifications sent automatically.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: ShieldCheck,
      title: 'Secure Payment Capture',
      desc: 'Tokenized payment data capture for failed charge retries and payment method updates - fully PCI-compliant with no raw card data exposed.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: Bell,
      title: 'Case Logging & Notifications',
      desc: 'Auto-create CRM cases for every billing interaction and send confirmation notifications via email or SMS once an action is completed.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: ArrowRight,
      title: 'Escalation for Chargebacks',
      desc: 'Chargeback and high-value dispute cases are routed to specialist billing agents with full interaction context, billing history, and prior resolution attempts.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Customer requests billing help',
      desc: 'The customer contacts via voice, chat, or email and is identified as a billing inquiry - invoice question, refund request, failed payment, or subscription change.',
    },
    {
      step: '02',
      title: 'AI verifies identity and entitlement',
      desc: 'The agent verifies the customer using configured verification methods and confirms entitlement to view or modify the referenced account and billing records.',
    },
    {
      step: '03',
      title: 'AI retrieves billing, invoice, and subscription data',
      desc: 'Live billing data is fetched from the connected platform - invoices, payment history, current plan, and any outstanding charges - displayed in context.',
    },
    {
      step: '04',
      title: 'AI executes the allowed action',
      desc: 'Refund initiation, plan change, invoice link delivery, or failed payment retry is executed within configured policy limits without agent involvement.',
    },
    {
      step: '05',
      title: 'AI logs the case and sends confirmation',
      desc: 'A CRM case is created with full billing context and action taken. The customer receives a confirmation notification via their preferred channel.',
    },
    {
      step: '06',
      title: 'Escalates disputes and chargeback scenarios',
      desc: 'High-friction cases - chargebacks, billing fraud, or out-of-policy exceptions - are routed to a billing specialist with complete context pre-loaded.',
    },
  ];

  const stats = [
    { value: '−60%', label: 'AHT reduction (8min→3.2min)' },
    { value: '+20pts', label: 'FCR improvement (65%→85%)' },
    { value: '+22%', label: 'Higher CSAT' },
    { value: '~68%', label: 'Deflection for routine billing' },
  ];

  const faqs = [
    {
      q: 'Can the AI take credit card information?',
      a: 'Yes, with tokenized and PCI-compliant handling. The agent captures payment data through a secure tokenization layer - raw card numbers are never stored or logged by the AI system. The token is passed directly to the configured payment gateway.',
    },
    {
      q: 'Can it handle region-specific billing rules?',
      a: 'Yes. Refund windows, tax handling, proration logic, and cancellation policies are fully configurable by region, currency, and product line. The agent evaluates the active ruleset for the customer\'s account before taking any billing action.',
    },
    {
      q: 'Does it support subscription pausing?',
      a: 'Yes, if the connected billing platform supports pause functionality (Stripe, Chargebee, Recurly, and others do). The agent can initiate a pause, set the resume date, and send the customer confirmation - all without agent involvement.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseBillingPayments" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Billing & Payments","item":"https://digian.ai/use-cases/billing-payments"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">Customer Care / Billing, Payments &amp; Subscriptions</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Billing Support - Payments, Refunds, Subscriptions (Omnichannel)
            </h1>
            <p className="text-lg text-slate-600">
              Automate high-friction billing inquiries and workflows across channels: invoice lookup, payment status, refunds, disputes intake, subscription upgrades/downgrades, and cancellation flows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['PCI-compliant payment handling', 'Real-time billing data', 'Policy-driven refunds'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Billing Support Automation</p>
                <p className="text-sm text-slate-600">Full billing lifecycle across voice, chat &amp; email</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <CreditCard className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why teams automate billing support</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate billing and payments</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI billing support works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Deflect 68% of billing contacts - starting now</h2>
            <p className="text-xl text-indigo-100">
              Invoices, refunds, plan changes, and payment failures - resolved automatically, at scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">PCI-compliant · Real-time billing data · Policy-driven automation</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BillingPaymentsPage;
